import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useRecordDeepLink from '../hooks/useRecordDeepLink';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Pagination,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import BusinessIcon from '@mui/icons-material/Business';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';

import clientService from '../services/clientService';
import ClientFormDialog from '../components/ClientFormDialog';
import EmailComposerDialog from '../components/EmailComposerDialog';
import { useAuth } from '../context/AuthContext';

const CLIENT_TABLE_PREFS_KEY = 'client-management-table-prefs-v1';
const MOBILE_CLIENTS_PER_PAGE = 6;

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'success', icon: CheckCircleIcon },
  inactive: { label: 'Inactive', color: 'error', icon: CancelIcon },
  prospect: { label: 'Prospect', color: 'warning', icon: HourglassEmptyIcon }
};

const STATUS_OPTIONS = ['all', 'active', 'prospect', 'inactive'];

const getLocationSummary = (client) => {
  const parts = [client?.city, client?.state, client?.country].filter(Boolean);
  return parts.length ? parts.join(', ') : 'No location';
};

const OUTGOING_EMAIL_FROM = process.env.REACT_APP_OUTGOING_EMAIL_FROM || 'SMTLINKS <sales@smtlinkss.com>';

const formatFileSize = (size = 0) => {
  if (!Number.isFinite(size) || size <= 0) return '0 KB';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const normalizeEmailPreview = (value) => String(value || '').replace(/\r\n/g, '\n').trim();
const getPreviewFirstLine = (value) => normalizeEmailPreview(value).split('\n').map((line) => line.trim()).find(Boolean) || '';
const getPreviewLastLines = (value, count = 3) => normalizeEmailPreview(value).split('\n').map((line) => line.trim()).filter(Boolean).slice(-count).join('\n');
const hasPreviewGreeting = (value) => /^(hello|hi|dear|good morning|good afternoon|good evening)\b/i.test(getPreviewFirstLine(value));
const hasPreviewClosing = (value) => /(best regards|kind regards|warm regards|regards|sincerely|thanks|thank you)/i.test(getPreviewLastLines(value));
const buildEmailPreviewBody = ({ message, recipientName, senderName, senderCompanyName }) => {
  let draft = normalizeEmailPreview(message);
  if (!draft) draft = `We are contacting you from ${senderCompanyName || 'SMTLINKS'}.`;
  if (!hasPreviewGreeting(draft)) {
    draft = `Hello ${recipientName},\n\n${draft}`;
  }
  if (!hasPreviewClosing(draft)) {
    draft = `${draft}\n\nBest regards,\n${senderName}`;
  }
  return draft.trim();
};

const EMAIL_TEMPLATE_OPTIONS = [
  { value: 'introduction', label: 'Introduction' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'documents', label: 'Share Documents' },
  { value: 'meeting', label: 'Meeting Request' },
  { value: 'custom', label: 'Custom Draft' }
];

const EMAIL_QUICK_INSERTS = [
  { label: 'Greeting', snippet: 'I hope you are doing well.' },
  { label: 'CTA', snippet: 'Please let me know if you have any questions.' },
  { label: 'Next Step', snippet: 'Please confirm the next step so we can continue.' },
  { label: 'Thanks', snippet: 'Thank you for your time and support.' },
  { label: 'Signature', snippet: 'Best regards,\n{sender_name}' }
];

const buildClientEmailDraft = ({ template, client, senderName }) => {
  const recipientLabel = client?.contact_person || client?.company_name || 'team';
  const companyName = client?.company_name || 'your team';
  const sender = senderName || 'SMTLINKS Team';

  switch (template) {
    case 'follow_up':
      return {
        subject: `Follow up - ${companyName}`,
        message: [
          `Hello ${recipientLabel},`,
          '',
          'I hope you are doing well.',
          '',
          'I am following up on our previous conversation to see if you need any additional information from our side.',
          '',
          'Please let me know how you would like to proceed.',
          '',
          'Best regards,',
          sender
        ].join('\n')
      };
    case 'documents':
      return {
        subject: `Requested documents - ${companyName}`,
        message: [
          `Hello ${recipientLabel},`,
          '',
          'Please find the requested information attached in this email.',
          '',
          'If you need any clarification or additional files, I will be glad to help.',
          '',
          'Best regards,',
          sender
        ].join('\n')
      };
    case 'meeting':
      return {
        subject: `Meeting request - ${companyName}`,
        message: [
          `Hello ${recipientLabel},`,
          '',
          'I hope you are doing well.',
          '',
          'I would like to schedule a short meeting to review the current status and next steps together.',
          '',
          'Please let me know your availability.',
          '',
          'Best regards,',
          sender
        ].join('\n')
      };
    case 'custom':
      return {
        subject: `SMTLINKS - ${companyName}`,
        message: [
          `Hello ${recipientLabel},`,
          '',
          '',
          'Best regards,',
          sender
        ].join('\n')
      };
    case 'introduction':
    default:
      return {
        subject: `SMTLINKS - ${companyName}`,
        message: [
          `Hello ${recipientLabel},`,
          '',
          'I hope you are doing well.',
          '',
          'I am reaching out to share the requested information and remain available for any questions you may have.',
          '',
          'Please let me know if you need anything else from our side.',
          '',
          'Best regards,',
          sender
        ].join('\n')
      };
  }
};

const normalizeClientResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.clients)) return data.clients;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
};

export default function ClientManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const accentColor = theme.palette.primary.main;
  const { user } = useAuth();

  const [uiPrefs, setUiPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(CLIENT_TABLE_PREFS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return {
        showFilters: parsed?.showFilters ?? false,
        showKpis: parsed?.showKpis ?? false,
        tablePageSize: parsed?.tablePageSize ?? 25,
        columnVisibilityModel: parsed?.columnVisibilityModel ?? {
          contact_person: true,
          email: true,
          phone: true,
          location: true,
          created_at: true
        }
      };
    } catch (error) {
      return {
        showFilters: false,
        showKpis: false,
        tablePageSize: 25,
        columnVisibilityModel: {
          contact_person: true,
          email: true,
          phone: true,
          location: true,
          created_at: true
        }
      };
    }
  });

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [desktopPaginationModel, setDesktopPaginationModel] = useState({
    page: 0,
    pageSize: uiPrefs.tablePageSize
  });
  const [mobilePage, setMobilePage] = useState(1);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTargetClient, setEmailTargetClient] = useState(null);
  const [sendingClientEmail, setSendingClientEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: '',
    cc: '',
    subject: '',
    message: ''
  });
  const [emailTemplate, setEmailTemplate] = useState('introduction');
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailHistoryLoading, setEmailHistoryLoading] = useState(false);
  const [emailHistoryError, setEmailHistoryError] = useState('');
  const canSendClientEmail = ['admin', 'manager'].includes(String(user?.role || '').toLowerCase());
  const currentUserDisplayName = useMemo(() => {
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    return fullName || user?.username || user?.email || 'SMTLINKS Team';
  }, [user?.email, user?.first_name, user?.last_name, user?.username]);
  const replyToEmail = user?.email || 'No email configured in your profile';
  const emailPreview = useMemo(() => buildEmailPreviewBody({
    message: emailForm.message,
    recipientName: emailTargetClient?.contact_person || emailTargetClient?.company_name || 'there',
    senderName: currentUserDisplayName,
    senderCompanyName: 'SMTLINKS'
  }), [currentUserDisplayName, emailForm.message, emailTargetClient?.company_name, emailTargetClient?.contact_person]);

  useEffect(() => {
    localStorage.setItem(CLIENT_TABLE_PREFS_KEY, JSON.stringify(uiPrefs));
  }, [uiPrefs]);

  const updateUiPrefs = useCallback((patch) => {
    setUiPrefs((prev) => ({ ...prev, ...patch }));
  }, []);

  const toggleColumnVisibility = useCallback((field) => {
    updateUiPrefs({
      columnVisibilityModel: {
        ...uiPrefs.columnVisibilityModel,
        [field]: !(uiPrefs.columnVisibilityModel?.[field] ?? true)
      }
    });
  }, [uiPrefs.columnVisibilityModel, updateUiPrefs]);

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const data = await clientService.getClients();
      setClients(normalizeClientResponse(data));
      setError('');
    } catch (err) {
      setError('Error loading clients');
      console.error(err);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (location.state?.openDialog) {
      setSelectedClient(null);
      setOpenDialog(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      const haystack = [
        client.company_name,
        client.contact_person,
        client.email,
        client.phone,
        client.city,
        client.country,
        client.tax_id
      ].filter(Boolean).join(' ').toLowerCase();

      const matchesSearch = !searchTerm.trim() || haystack.includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [clients, searchTerm, filterStatus]);

  const hasActiveFilters = Boolean(searchTerm.trim() || filterStatus !== 'all');
  const mobilePageCount = Math.max(1, Math.ceil(filteredClients.length / MOBILE_CLIENTS_PER_PAGE));
  const paginatedMobileClients = useMemo(() => {
    if (!isMobile) return filteredClients;
    const startIndex = (mobilePage - 1) * MOBILE_CLIENTS_PER_PAGE;
    return filteredClients.slice(startIndex, startIndex + MOBILE_CLIENTS_PER_PAGE);
  }, [filteredClients, isMobile, mobilePage]);

  useEffect(() => {
    setMobilePage(1);
  }, [filterStatus, searchTerm]);

  useEffect(() => {
    setMobilePage((current) => Math.min(current, mobilePageCount));
  }, [mobilePageCount]);

  const stats = useMemo(() => ({
    total: clients.length,
    active: clients.filter((client) => client.status === 'active').length,
    prospect: clients.filter((client) => client.status === 'prospect').length,
    inactive: clients.filter((client) => client.status === 'inactive').length
  }), [clients]);

  const handleAddClient = useCallback(() => {
    setSelectedClient(null);
    setOpenDialog(true);
  }, []);

  const handleEditClient = useCallback((client) => {
    setSelectedClient(client);
    setOpenDialog(true);
  }, []);

  const handleViewClient = useCallback((client) => {
    setSelectedClient(client);
    setOpenViewDialog(true);
  }, []);

  useRecordDeepLink({ parameter: 'client_id', userId: user?.id,
    readRecord: async id => (await clientService.getClientById(id)).client,
    onOpen: handleViewClient, onError: setError
  });

  const handleDeleteClick = useCallback((client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;

    try {
      await clientService.deleteClient(clientToDelete.id);
      await loadClients();
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    } catch (err) {
      setError('Error deleting client');
      console.error(err);
    }
  };

  const handleSaveClient = async (clientData) => {
    try {
      if (selectedClient) {
        await clientService.updateClient(selectedClient.id, clientData);
      } else {
        await clientService.createClient(clientData);
      }
      await loadClients();
      setOpenDialog(false);
    } catch (err) {
      setError(err?.response?.data?.message || 'Error saving client');
      console.error(err);
      throw err;
    }
  };

  const loadClientEmailHistory = useCallback(async (clientId) => {
    if (!clientId) return;
    setEmailHistoryLoading(true);
    setEmailHistoryError('');
    try {
      const response = await clientService.getEmailHistory(clientId);
      setEmailHistory(Array.isArray(response?.items) ? response.items : []);
    } catch (err) {
      setEmailHistory([]);
      setEmailHistoryError(
        err?.response?.data?.message || 'The sent email history could not be loaded.'
      );
    } finally {
      setEmailHistoryLoading(false);
    }
  }, []);

  const handleOpenSendEmailDialog = useCallback((client) => {
    if (!client) return;
    const nextTemplate = 'introduction';
    const draft = buildClientEmailDraft({
      template: nextTemplate,
      client,
      senderName: currentUserDisplayName
    });
    setEmailTargetClient(client);
    setEmailTemplate(nextTemplate);
    setEmailForm({
      to: client.email || '',
      cc: '',
      subject: draft.subject,
      message: draft.message
    });
    setEmailAttachments([]);
    setEmailHistory([]);
    setEmailHistoryError('');
    setEmailDialogOpen(true);
    loadClientEmailHistory(client.id);
  }, [currentUserDisplayName, loadClientEmailHistory]);

  const handleCloseEmailDialog = useCallback(() => {
    setEmailDialogOpen(false);
    setEmailTargetClient(null);
    setEmailTemplate('introduction');
    setEmailAttachments([]);
    setEmailHistory([]);
    setEmailHistoryError('');
    setEmailHistoryLoading(false);
  }, []);

  const handleApplyEmailTemplate = useCallback((template) => {
    if (!emailTargetClient) return;
    const draft = buildClientEmailDraft({
      template,
      client: emailTargetClient,
      senderName: currentUserDisplayName
    });

    setEmailTemplate(template);
    setEmailForm((prev) => ({
      ...prev,
      subject: draft.subject,
      message: draft.message
    }));
  }, [currentUserDisplayName, emailTargetClient]);

  const handleInsertEmailSnippet = useCallback((snippet) => {
    const resolvedSnippet = String(snippet || '').replaceAll('{sender_name}', currentUserDisplayName);
    setEmailForm((prev) => {
      const currentMessage = String(prev.message || '');
      const needsBreak = currentMessage.length > 0 && !currentMessage.endsWith('\n');
      return {
        ...prev,
        message: `${currentMessage}${needsBreak ? '\n' : ''}${resolvedSnippet}`
      };
    });
  }, [currentUserDisplayName]);

  const handleEmailAttachmentChange = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setEmailAttachments((prev) => {
      const existingKeys = new Set(prev.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
      const next = [...prev];
      files.forEach((file) => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (!existingKeys.has(key)) {
          existingKeys.add(key);
          next.push(file);
        }
      });
      return next;
    });

    event.target.value = '';
  }, []);

  const handleRemoveEmailAttachment = useCallback((indexToRemove) => {
    setEmailAttachments((prev) => prev.filter((_, index) => index !== indexToRemove));
  }, []);

  const handleSendClientEmail = useCallback(async () => {
    if (!emailTargetClient?.id) return;
    try {
      setSendingClientEmail(true);
      const response = await clientService.sendClientEmail(emailTargetClient.id, {
        ...emailForm,
        attachments: emailAttachments
      });
      if (!response?.success) throw new Error(response?.message || 'The email could not be sent');
      setError('');
      setSuccessMessage(`Email sent to ${response.sent_to?.join(', ') || emailTargetClient.email || 'client'}${response.attachment_count ? ` with ${response.attachment_count} attachment(s)` : ''}`);
      handleCloseEmailDialog();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Error sending client email');
    } finally {
      setSendingClientEmail(false);
    }
  }, [emailAttachments, emailForm, emailTargetClient, handleCloseEmailDialog]);

  const columns = useMemo(() => [
    {
      field: 'company_name',
      headerName: 'Client',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.row.company_name || 'No company'}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}
          >
            {params.row.company_name}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'contact_person',
      headerName: 'Contact',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Tooltip title={params.row.contact_person || 'No contact'}>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}
          >
            {params.row.contact_person || 'No contact'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const config = STATUS_CONFIG[params.value] || STATUS_CONFIG.active;
        return (
          <Chip
            label={config.label}
            color={config.color}
            size="small"
            sx={{ fontWeight: 700, minWidth: 100 }}
          />
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Tooltip title={params.value || '-'}>
          <Typography
            variant="body2"
            sx={{
              color: 'primary.main',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}
          >
            {params.value || '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 180,
      valueGetter: (_, row) => getLocationSummary(row),
      renderCell: (params) => (
        <Tooltip title={params.value || 'No location'}>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Registered',
      width: 130,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (value) => (
        value
          ? new Date(value).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
          : ''
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      disableColumnMenu: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View details">
            <IconButton size="small" onClick={() => handleViewClient(params.row)} sx={{ color: 'info.main' }}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEditClient(params.row)} sx={{ color: 'primary.main' }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={canSendClientEmail ? 'Send email' : 'Only admin/manager can send emails'}>
            <span>
              <IconButton
                size="small"
                onClick={() => handleOpenSendEmailDialog(params.row)}
                sx={{ color: 'secondary.main' }}
                disabled={!canSendClientEmail || !params.row.email}
              >
                <EmailIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDeleteClick(params.row)} sx={{ color: 'error.main' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [canSendClientEmail, handleDeleteClick, handleEditClient, handleOpenSendEmailDialog, handleViewClient]);

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card
      sx={{
        height: '100%',
        border: '1px solid',
        borderColor: `${color}.main`,
        borderRadius: 2.5,
        background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.14)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        boxShadow: `0 12px 28px ${alpha(theme.palette[color].main, 0.08)}`,
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 34px ${alpha(theme.palette[color].main, 0.14)}`
        }
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: `${color}.main` }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: `${color}.main`, width: 42, height: 42 }}>
            <Icon />
          </Avatar>
        </Stack>
      </CardContent>
    </Card>
  );

  const ClientCard = ({ client }) => {
    const config = STATUS_CONFIG[client.status] || STATUS_CONFIG.active;

    return (
      <Card
        sx={{
          mb: 1.5,
          transition: 'all 0.3s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 }
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.35, overflowWrap: 'anywhere' }}>
                  {client.company_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                  {client.contact_person || 'No contact person'}
                </Typography>
              </Box>
              <Chip label={config.label} color={config.color} size="small" sx={{ fontWeight: 700, flexShrink: 0 }} />
            </Box>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip label={getLocationSummary(client)} variant="outlined" size="small" />
              <Chip
                label={client.created_at ? new Date(client.created_at).toLocaleDateString('en-US') : 'No date'}
                variant="outlined"
                size="small"
              />
            </Stack>

            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Contact information
              </Typography>
              <Typography
                component={client.email ? 'a' : 'span'}
                href={client.email ? `mailto:${client.email}` : undefined}
                variant="body2"
                color="primary.main"
                sx={{ textDecoration: 'none', overflowWrap: 'anywhere' }}
              >
                {client.email || '-'}
              </Typography>
              {client.phone && (
                <Typography component="a" href={`tel:${client.phone}`} variant="body2" color="text.primary" sx={{ textDecoration: 'none' }}>
                  {client.phone}
                </Typography>
              )}
            </Stack>

            <Divider />

            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button size="small" startIcon={<VisibilityIcon />} onClick={() => handleViewClient(client)} sx={{ px: 1 }}>
                View
              </Button>
              <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditClient(client)} sx={{ px: 1 }}>
                Edit
              </Button>
              <Button
                size="small"
                startIcon={<EmailIcon />}
                onClick={() => handleOpenSendEmailDialog(client)}
                disabled={!canSendClientEmail || !client.email}
                sx={{ px: 1 }}
              >
                Email
              </Button>
              <IconButton size="small" color="error" onClick={() => handleDeleteClick(client)}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={56} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.4, sm: 1.7 },
          mb: 1.5,
          borderRadius: 2.5,
          color: 'white',
          border: '1px solid',
          borderColor: alpha(accentColor, 0.28),
          backgroundImage: `
            radial-gradient(circle at top right, ${alpha('#ffffff', 0.18)} 0%, transparent 28%),
            linear-gradient(135deg, ${alpha(accentColor, 0.95)} 0%, ${alpha(theme.palette.primary.dark, 0.98)} 100%)
          `,
          boxShadow: `0 10px 24px ${alpha(accentColor, 0.16)}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Avatar
              sx={{
                bgcolor: alpha('#ffffff', 0.16),
                width: 42,
                height: 42,
                boxShadow: `0 10px 24px ${alpha('#000', 0.18)}`
              }}
            >
              <PeopleIcon sx={{ fontSize: 23 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.2 }}>
                Clients
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.88 }}>
                Manage clients, contacts, and fiscal information
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
            <Chip
              label={`${stats.total} clients`}
              sx={{
                bgcolor: alpha('#ffffff', 0.14),
                color: 'white',
                border: '1px solid',
                borderColor: alpha('#ffffff', 0.18),
                fontWeight: 700
              }}
            />
            {!isMobile && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddCircleIcon />}
                onClick={handleAddClient}
                sx={{
                  px: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  color: 'primary.main',
                  bgcolor: '#fff',
                  boxShadow: `0 10px 24px ${alpha('#000', 0.14)}`,
                  '&:hover': {
                    bgcolor: alpha('#ffffff', 0.94)
                  }
                }}
              >
                New Client
              </Button>
            )}
          </Stack>
        </Box>
      </Paper>

      <Paper sx={{ p: { xs: 1.25, sm: 2 }, mb: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1} useFlexGap sx={{ minWidth: 0, overflowX: { xs: 'auto', sm: 'visible' }, pb: { xs: 0.5, sm: 0 }, '& .MuiChip-root': { flexShrink: 0 } }}>
            <Chip label={`Total ${stats.total}`} color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
            <Chip label={`Active ${stats.active}`} color="success" variant="outlined" sx={{ fontWeight: 700 }} />
            <Chip label={`Prospects ${stats.prospect}`} color="warning" variant="outlined" sx={{ fontWeight: 700 }} />
            <Chip label={`Inactive ${stats.inactive}`} color="error" variant="outlined" sx={{ fontWeight: 700 }} />
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap sx={{ '& .MuiButton-root': { flex: { xs: 1, sm: 'initial' } } }}>
            <Button
              size="small"
              variant={uiPrefs.showFilters ? 'contained' : 'outlined'}
              onClick={() => updateUiPrefs({ showFilters: !uiPrefs.showFilters })}
              endIcon={uiPrefs.showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              Filters
            </Button>
            <Button
              size="small"
              variant={uiPrefs.showKpis ? 'contained' : 'outlined'}
              onClick={() => updateUiPrefs({ showKpis: !uiPrefs.showKpis })}
              endIcon={uiPrefs.showKpis ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              KPIs
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Collapse in={uiPrefs.showFilters}>
        <Paper
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: alpha(accentColor, 0.16),
            backgroundImage: `linear-gradient(180deg, ${alpha(accentColor, 0.04)} 0%, transparent 100%)`
          }}
        >
          <Stack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems="center">
            <TextField
              placeholder="Search company, contact, email, phone..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              select
              label="Status"
              value={filterStatus}
              onChange={(event) => setFilterStatus(event.target.value)}
              size="small"
              sx={{ minWidth: 180, width: isMobile ? '100%' : 'auto' }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option === 'all' ? 'All' : STATUS_CONFIG[option]?.label || option}
                </MenuItem>
              ))}
            </TextField>

            <Button startIcon={<RefreshIcon />} onClick={loadClients} variant="outlined" size="small">
              Refresh
            </Button>

            {hasActiveFilters && (
              <Button
                startIcon={<FilterAltOffIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                }}
                variant="outlined"
                size="small"
              >
                Clear
              </Button>
            )}

            <Chip
              label={`${filteredClients.length} result${filteredClients.length === 1 ? '' : 's'}`}
              color="primary"
              variant="outlined"
            />
          </Stack>
        </Paper>
      </Collapse>

      <Collapse in={uiPrefs.showKpis}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={PeopleIcon} label="Total Clients" value={stats.total} color="primary" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={CheckCircleIcon} label="Active" value={stats.active} color="success" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={HourglassEmptyIcon} label="Prospects" value={stats.prospect} color="warning" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={CancelIcon} label="Inactive" value={stats.inactive} color="error" />
          </Grid>
        </Grid>
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 1.25, sm: 2, md: 3 }, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Clients List
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Focus on the contacts and accounts that matter most right now.
              </Typography>
            </Box>

            {!isMobile && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  clickable
                  label={`Contact ${uiPrefs.columnVisibilityModel.contact_person ? 'On' : 'Off'}`}
                  color={uiPrefs.columnVisibilityModel.contact_person ? 'primary' : 'default'}
                  variant={uiPrefs.columnVisibilityModel.contact_person ? 'filled' : 'outlined'}
                  onClick={() => toggleColumnVisibility('contact_person')}
                />
                <Chip
                  clickable
                  label={`Email ${uiPrefs.columnVisibilityModel.email ? 'On' : 'Off'}`}
                  color={uiPrefs.columnVisibilityModel.email ? 'primary' : 'default'}
                  variant={uiPrefs.columnVisibilityModel.email ? 'filled' : 'outlined'}
                  onClick={() => toggleColumnVisibility('email')}
                />
                <Chip
                  clickable
                  label={`Phone ${uiPrefs.columnVisibilityModel.phone ? 'On' : 'Off'}`}
                  color={uiPrefs.columnVisibilityModel.phone ? 'primary' : 'default'}
                  variant={uiPrefs.columnVisibilityModel.phone ? 'filled' : 'outlined'}
                  onClick={() => toggleColumnVisibility('phone')}
                />
                <Chip
                  clickable
                  label={`Location ${uiPrefs.columnVisibilityModel.location ? 'On' : 'Off'}`}
                  color={uiPrefs.columnVisibilityModel.location ? 'primary' : 'default'}
                  variant={uiPrefs.columnVisibilityModel.location ? 'filled' : 'outlined'}
                  onClick={() => toggleColumnVisibility('location')}
                />
                <Chip
                  clickable
                  label={`Registered ${uiPrefs.columnVisibilityModel.created_at ? 'On' : 'Off'}`}
                  color={uiPrefs.columnVisibilityModel.created_at ? 'primary' : 'default'}
                  variant={uiPrefs.columnVisibilityModel.created_at ? 'filled' : 'outlined'}
                  onClick={() => toggleColumnVisibility('created_at')}
                />
              </Stack>
            )}
          </Stack>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {filteredClients.length === 0 ? (
          <Box sx={{ py: 6, px: 2, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
            <PeopleIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {hasActiveFilters ? 'No clients match your filters' : 'No clients yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {hasActiveFilters
                ? 'Try clearing filters or adjusting the search text.'
                : 'Create your first client to start linking projects and documents.'}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="center">
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('all');
                  }}
                >
                  Clear filters
                </Button>
              )}
              <Button variant="contained" startIcon={<AddCircleIcon />} onClick={handleAddClient}>
                New Client
              </Button>
            </Stack>
          </Box>
        ) : isMobile ? (
          <Box>
            {paginatedMobileClients.map((client) => (
              <ClientCard key={client.id} client={client} />
            ))}
            {mobilePageCount > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, overflow: 'hidden' }}>
                <Pagination
                  count={mobilePageCount}
                  page={mobilePage}
                  onChange={(_event, value) => setMobilePage(value)}
                  color="primary"
                  size="small"
                  siblingCount={0}
                  boundaryCount={1}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ height: 640, width: '100%' }}>
            <DataGrid
              rows={filteredClients}
              columns={columns}
              columnVisibilityModel={uiPrefs.columnVisibilityModel}
              onColumnVisibilityModelChange={(model) => updateUiPrefs({ columnVisibilityModel: model })}
              paginationModel={desktopPaginationModel}
              onPaginationModelChange={(model) => {
                setDesktopPaginationModel(model);
                if (model.pageSize !== uiPrefs.tablePageSize) {
                  updateUiPrefs({ tablePageSize: model.pageSize });
                }
              }}
              density="compact"
              rowHeight={42}
              columnHeaderHeight={44}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  py: 0.4,
                  alignItems: 'center'
                },
                '& .MuiDataGrid-row:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.03)
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.primary.main, 0.025)
                }
              }}
            />
          </Box>
        )}
      </Paper>

      {isMobile && (
        <Box sx={{ position: 'fixed', bottom: 'calc(76px + env(safe-area-inset-bottom, 0px))', right: 16, zIndex: 1000 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleAddClient}
            aria-label="Create new client"
            sx={{ borderRadius: '50%', minWidth: 56, width: 56, height: 56, p: 0, boxShadow: 4 }}
          >
            <PersonAddIcon />
          </Button>
        </Box>
      )}

      <ClientFormDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        client={selectedClient}
        onSave={handleSaveClient}
      />

      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        {selectedClient && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 52, height: 52 }}>
                <BusinessIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {selectedClient.company_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Client #{selectedClient.id}
                </Typography>
              </Box>
              {isMobile && (
                <IconButton aria-label="Close client details" onClick={() => setOpenViewDialog(false)} sx={{ flexShrink: 0 }}>
                  <CloseIcon />
                </IconButton>
              )}
            </DialogTitle>

            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <Stack spacing={3}>
                <Box>
                  <Chip
                    label={(STATUS_CONFIG[selectedClient.status] || STATUS_CONFIG.active).label}
                    color={(STATUS_CONFIG[selectedClient.status] || STATUS_CONFIG.active).color}
                    sx={{ fontWeight: 700 }}
                  />
                </Box>

                <Divider />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Contact</Typography>
                    <Typography variant="body1">{selectedClient.contact_person || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body1" sx={{ color: 'primary.main', wordBreak: 'break-word' }}>
                      {selectedClient.email || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Phone</Typography>
                    <Typography variant="body1">{selectedClient.phone || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Location</Typography>
                    <Typography variant="body1">{getLocationSummary(selectedClient)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Address</Typography>
                    <Typography variant="body1">{selectedClient.address || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Website</Typography>
                    <Typography variant="body1">{selectedClient.website || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">RFC / Tax ID</Typography>
                    <Typography variant="body1">{selectedClient.tax_id || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedClient.notes || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Registered</Typography>
                    <Typography variant="body1">
                      {selectedClient.created_at ? new Date(selectedClient.created_at).toLocaleDateString('en-US') : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Stack>
            </DialogContent>

            <DialogActions sx={{
              px: { xs: 2, sm: 2.5 },
              pt: 1.5,
              pb: isMobile ? 'max(12px, env(safe-area-inset-bottom, 0px))' : 2.5,
              gap: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              '& .MuiButton-root': { flex: { xs: 1, sm: 'initial' }, minHeight: 44 },
              '& .MuiButton-root + .MuiButton-root': { ml: 0 }
            }}>
              <Button onClick={() => setOpenViewDialog(false)}>
                Close
              </Button>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                disabled={!canSendClientEmail || !selectedClient?.email}
                onClick={() => {
                  const activeClient = selectedClient;
                  setOpenViewDialog(false);
                  handleOpenSendEmailDialog(activeClient);
                }}
              >
                Send Email
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  setOpenViewDialog(false);
                  handleEditClient(selectedClient);
                }}
              >
                Edit Client
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={false}
        onClose={() => {
          if (sendingClientEmail) return;
          handleCloseEmailDialog();
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: alpha(accentColor, 0.22),
            backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.99)} 0%, ${alpha(theme.palette.background.paper, 0.96)} 100%)`,
            boxShadow: `0 24px 60px ${alpha('#000', 0.42)}`
          }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid',
            borderColor: alpha(accentColor, 0.18),
            backgroundImage: `linear-gradient(120deg, ${alpha(accentColor, 0.18)} 0%, ${alpha(theme.palette.background.paper, 0.08)} 100%)`
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: alpha(accentColor, 0.18),
                  color: accentColor,
                  boxShadow: `0 10px 24px ${alpha(accentColor, 0.22)}`
                }}
              >
                <EmailIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
                  Send Client Email
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {emailTargetClient?.company_name || 'Client message'}
                </Typography>
              </Box>
            </Stack>

            <Chip
              size="small"
              label={emailTargetClient?.status ? String(emailTargetClient.status).toUpperCase() : 'EMAIL'}
              sx={{
                border: '1px solid',
                borderColor: alpha(accentColor, 0.22),
                bgcolor: alpha(accentColor, 0.08),
                fontWeight: 700
              }}
            />
          </Stack>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            py: 3,
            bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.44 : 0.58)
          }}
        >
          <Grid container spacing={2.2}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: alpha(accentColor, 0.18),
                  bgcolor: alpha(accentColor, 0.07)
                }}
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      FROM
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                      {OUTGOING_EMAIL_FROM}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      REPLY-TO
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                      {replyToEmail}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      CLIENT
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                      {emailTargetClient?.company_name || 'No client selected'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="To"
                value={emailForm.to}
                onChange={(e) => setEmailForm((prev) => ({ ...prev, to: e.target.value }))}
                placeholder="client@example.com"
                helperText="Separate multiple email addresses with a comma or semicolon."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="CC (Optional)"
                value={emailForm.cc}
                onChange={(e) => setEmailForm((prev) => ({ ...prev, cc: e.target.value }))}
                placeholder="team@example.com; sales@example.com"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Template"
                value={emailTemplate}
                onChange={(e) => handleApplyEmailTemplate(e.target.value)}
                helperText="Aplica una estructura base y despues la puedes editar libremente."
              >
                {EMAIL_TEMPLATE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Subject"
                value={emailForm.subject}
                onChange={(e) => setEmailForm((prev) => ({ ...prev, subject: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: alpha(accentColor, 0.18),
                  bgcolor: alpha(accentColor, 0.04)
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 1 }}>
                  QUICK INSERTS
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {EMAIL_QUICK_INSERTS.map((item) => (
                    <Button
                      key={item.label}
                      size="small"
                      variant="outlined"
                      onClick={() => handleInsertEmailSnippet(item.snippet)}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      {item.label}
                    </Button>
                  ))}
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handleApplyEmailTemplate(emailTemplate)}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Reset draft
                  </Button>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                multiline
                minRows={10}
                label="Message"
                value={emailForm.message}
                onChange={(e) => setEmailForm((prev) => ({ ...prev, message: e.target.value }))}
                helperText={`Se enviara como ${currentUserDisplayName} usando el buzon corporativo.`}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack spacing={2}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.85),
                    bgcolor: alpha(theme.palette.background.paper, 0.48)
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Client Summary
                  </Typography>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Contact</Typography>
                      <Typography variant="body2">{emailTargetClient?.contact_person || 'Not defined'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {emailTargetClient?.email || 'No client email registered'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography variant="body2">{emailTargetClient?.phone || 'Not defined'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Location</Typography>
                      <Typography variant="body2">{getLocationSummary(emailTargetClient)}</Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: '1px dashed',
                    borderColor: alpha(accentColor, 0.4),
                    bgcolor: alpha(accentColor, 0.05)
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.2 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Attachments
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        PDF, Office, images, XML, TXT or CSV
                      </Typography>
                    </Box>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      Add files
                      <input
                        hidden
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.xml,.txt,.csv"
                        onChange={handleEmailAttachmentChange}
                      />
                    </Button>
                  </Stack>
                  {emailAttachments.length > 0 ? (
                    <Stack spacing={1}>
                      {emailAttachments.map((file, index) => (
                        <Chip
                          key={`${file.name}-${file.size}-${file.lastModified}`}
                          label={`${file.name} - ${formatFileSize(file.size)}`}
                          onDelete={() => handleRemoveEmailAttachment(index)}
                          sx={{
                            justifyContent: 'space-between',
                            height: 'auto',
                            '& .MuiChip-label': {
                              display: 'block',
                              whiteSpace: 'normal',
                              py: 0.75
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No attachments selected yet.
                    </Typography>
                  )}
                </Box>

                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  The email will be sent through the corporate SMTP service. Client replies will be delivered to <strong>{replyToEmail}</strong>.
                </Alert>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.85),
                    bgcolor: alpha(theme.palette.background.paper, 0.48)
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Message Preview
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Styled preview of how the client will receive the email.
                  </Typography>
                  <Box
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: alpha(accentColor, 0.16),
                      bgcolor: alpha(theme.palette.background.default, 0.58)
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1.15,
                        color: '#fff',
                        backgroundImage: 'linear-gradient(135deg, #123b69 0%, #1f7aa8 100%)'
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        SMTLINKS Client Email
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.92 }}>
                        From {OUTGOING_EMAIL_FROM}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1.75 }}>
                      <Stack spacing={1.4}>
                        {emailPreview
                          ? emailPreview.split(/\n{2,}/).map((block, index) => (
                            <Typography
                              key={`preview-block-${index}`}
                              variant="body2"
                              sx={{ whiteSpace: 'pre-line', lineHeight: 1.75 }}
                            >
                              {block}
                            </Typography>
                          ))
                          : (
                            <Typography variant="body2" color="text.secondary">
                              No message yet.
                            </Typography>
                          )}
                        <Box
                          sx={{
                            mt: 1,
                            pt: 1.5,
                            borderTop: '1px solid',
                            borderColor: alpha(theme.palette.divider, 0.7)
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
                            Reply route
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.4, wordBreak: 'break-word' }}>
                            Replies will go to {replyToEmail}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: alpha(accentColor, 0.18),
            bgcolor: alpha(theme.palette.background.paper, 0.9)
          }}
        >
          <Button
            onClick={handleCloseEmailDialog}
            disabled={sendingClientEmail}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={sendingClientEmail ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
            onClick={handleSendClientEmail}
            disabled={sendingClientEmail || !emailForm.to.trim()}
          >
            {sendingClientEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      <EmailComposerDialog
        open={emailDialogOpen}
        onClose={handleCloseEmailDialog}
        onSend={handleSendClientEmail}
        sending={sendingClientEmail}
        title="Send Client Email"
        subtitle={emailTargetClient?.company_name || ''}
        badge="CLIENT"
        form={emailForm}
        onFormChange={setEmailForm}
        template={emailTemplate}
        templateOptions={EMAIL_TEMPLATE_OPTIONS}
        onTemplateChange={handleApplyEmailTemplate}
        quickInserts={EMAIL_QUICK_INSERTS}
        onQuickInsert={handleInsertEmailSnippet}
        onResetDraft={() => handleApplyEmailTemplate(emailTemplate)}
        senderLabel={OUTGOING_EMAIL_FROM}
        replyToEmail={replyToEmail}
        contextLabel="CLIENT"
        contextValue={emailTargetClient?.company_name || '-'}
        senderName={currentUserDisplayName}
        previewText={emailPreview}
        summaryTitle="Client Summary"
        summaryItems={[
          { label: 'Contact', value: emailTargetClient?.contact_person },
          { label: 'Email', value: emailTargetClient?.email },
          { label: 'Phone', value: emailTargetClient?.phone },
          { label: 'Location', value: getLocationSummary(emailTargetClient) }
        ]}
        attachments={emailAttachments.map((file) => ({ file, source: 'manual' }))}
        onFilesSelected={handleEmailAttachmentChange}
        onRemoveAttachment={handleRemoveEmailAttachment}
        history={emailHistory}
        historyLoading={emailHistoryLoading}
        historyError={emailHistoryError}
        onRefreshHistory={() => loadClientEmailHistory(emailTargetClient?.id)}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Delete client
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{clientToDelete?.company_name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, '& .MuiButton-root': { flex: { xs: 1, sm: 'initial' } } }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4500}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSuccessMessage('')}
          severity="success"
          variant="filled"
          sx={{ width: '100%', boxShadow: 6 }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
